import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AlertFacade } from '@curbnturf/alert';
import { MiscSvgComponent } from '@curbnturf/custom-svgs/src/lib/misc-svg/misc-svg.component';
import { ITax, Listing, Role, Tax, User } from '@curbnturf/entities';
import {
  DisplayValidationErrorsDirective,
  DisplayValidationErrorsSubmitDirective,
  ITaxForm,
  ToggleComponent,
} from '@curbnturf/form-helpers';
import { ListingFacade } from '@curbnturf/listing';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-listing-taxes',
  templateUrl: './listing-taxes.component.html',
  styleUrls: ['./listing-taxes.component.scss'],
  standalone: true,
  imports: [
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsSubmitDirective,
    FormsModule,
    MiscSvgComponent,
    NgClass,
    NgFor,
    NgIf,
    ReactiveFormsModule,
    ToggleComponent,
  ],
})
export class ListingTaxesComponent implements OnDestroy, OnChanges {
  @Input() userId?: number;
  @Output() saved: EventEmitter<boolean> = new EventEmitter<boolean>();

  user: User;
  taxes: Tax[] = [];
  taxForms: FormGroup<{ taxes: FormArray<FormGroup<ITaxForm>> }>;
  listings: Listing[];
  changedListingIds: number[] = [];
  errors: string[];

  collectTaxes: FormControl<boolean | null>;

  subscriptions: Subscription[] = [];

  get taxControls(): FormArray<FormGroup<ITaxForm>> {
    return this.taxForms?.controls.taxes ?? this.fb.array<FormGroup<ITaxForm>>([]);
  }

  constructor(
    private fb: FormBuilder,
    private userFacade: UserFacade,
    private listingFacade: ListingFacade,
    private alertFacade: AlertFacade,
  ) {
    this.makeTaxForm();

    this.collectTaxes = this.fb.control<boolean | null>(null);
  }

  private makeTaxForm() {
    this.taxForms = this.fb.group({
      taxes: this.fb.array<FormGroup<ITaxForm>>([], { updateOn: 'blur' }),
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.userId && changes.userId.currentValue) {
      const userId = changes.userId.currentValue;
      this.subscriptions.push(
        this.userFacade.currentUser$.subscribe((currentUser) => {
          if (currentUser.id === userId) {
            return this.setUser(currentUser);
          }

          if (currentUser.role === Role.admin) {
            this.subscriptions.push(
              this.userFacade.allUserEntities$.subscribe((userEntities) => {
                if (userEntities[userId]) {
                  return this.setUser(userEntities[userId]);
                } else {
                  this.userFacade.load({ id: userId });

                  this.subscriptions.push(
                    this.userFacade.allUserEntities$.subscribe((users) => {
                      if (users[userId]) {
                        this.setUser(users[userId]);
                      }
                    }),
                  );
                }
              }),
            );
          }
        }),
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  ensureOneTax() {
    if (this.taxForms.controls.taxes.length === 0) {
      setTimeout(() => this.addTaxForm());
    }
  }

  listingHaveTaxes(listing: Listing, tax?: ITax): boolean {
    return !!(listing.taxes && listing.taxes.find((siteTax) => siteTax.id === tax?.id));
  }

  toggleListingTaxes(listing: Listing, tax: ITax) {
    if (listing.taxes && listing.id) {
      const taxes = [...listing.taxes];

      if (this.listingHaveTaxes(listing, tax)) {
        taxes.splice(
          taxes.findIndex((siteTax) => siteTax.id === tax.id),
          1,
        );
      } else {
        taxes.push(tax);
      }

      const listingIndex = this.listings.findIndex((compareListing) => compareListing.id === listing.id);

      this.listings.splice(listingIndex, 1, new Listing({ ...listing, taxes }));
      this.changedListingIds.push(listing.id);

      this.save();
    }
  }

  save() {
    if (this.user) {
      // save user (collectTaxes field)
      if (this.user?.collectTaxes !== this.collectTaxes.value || this.taxesUpdated()) {
        const taxes =
          this.taxForms.controls.taxes.value?.map((tax) => {
            return {
              id: tax.id || 0,
              name: tax.name || '',
              rate: tax.rate || 0,
            };
          }) || [];

        this.userFacade.update({
          ...this.user,
          collectTaxes: this.collectTaxes.value !== null ? this.collectTaxes.value : undefined,
          taxes,
        });
        this.setTaxes(taxes.map((tax) => new Tax(tax)));
        this.saved.emit(true);
      }

      // save what sites now have the tax
      const uniqueSiteIds = Object.keys(
        this.changedListingIds.reduce(
          (uniqueIds, id) => {
            uniqueIds[id] = id;
            return uniqueIds;
          },
          {} as { [id: number]: number },
        ),
      );

      if (this.listings) {
        this.listings.forEach((listing) => {
          if (uniqueSiteIds.includes(listing.id + '')) {
            this.listingFacade.update(listing);
          }
        });
      }

      this.changedListingIds.length = 0;
    } else {
      this.alertFacade.display({
        title: 'Information Missing',
        body: 'An error occurred and your request has been aborted.',
        level: 'error',
      });
    }
  }

  addTaxForm(tax?: Tax) {
    const taxForm = this.fb.group(
      {
        id: this.fb.control<number | null>(null),
        name: ['', [Validators.required]],
        rate: this.fb.control<number | null>(null, [Validators.required, Validators.min(0), Validators.max(100)]),
      },
      { updateOn: 'blur' },
    );

    if (tax) {
      taxForm.patchValue(tax);
    }

    this.taxForms.controls.taxes.push(taxForm);

    if (!tax) {
      this.save();
    }
  }

  removeTaxForm(tax?: ITax) {
    if (tax) {
      const taxIndex = this.taxForms.controls.taxes.controls.findIndex((taxForm) => taxForm.value.id === tax.id);
      this.taxForms.controls.taxes.removeAt(taxIndex);
      this.save();
    }
  }

  private taxesUpdated(): boolean {
    if (this.taxes.length !== this.taxForms.controls.taxes.length) {
      return true;
    }

    let updated = false;
    this.taxes.forEach((tax: Tax, index: number) => {
      const formValue = this.taxForms.controls.taxes.at(index).value;
      if (tax.name !== formValue.name || tax.rate !== formValue.rate) {
        updated = true;
      }
    });

    return updated;
  }

  private setTaxes(taxes?: Tax[]) {
    if (!taxes) {
      taxes = [];
    }

    this.taxes = taxes;

    this.makeTaxForm();

    if (this.taxes.length > 0) {
      this.taxes.forEach((tax: Tax) => {
        this.addTaxForm(tax);
      });
    }

    this.subscriptions.push(this.taxForms.valueChanges.subscribe(() => this.save()));
  }

  private setUser(user: User) {
    this.user = user;
    const collectTaxes = this.user.collectTaxes !== undefined ? this.user.collectTaxes : null;

    this.collectTaxes.setValue(collectTaxes);
    if (this.user.collectTaxes) {
      this.setTaxes(this.user.taxes);
    }
    this.listingFacade.loadUserListings(this.user.id);

    this.subscriptions.push(
      this.listingFacade.listings$.subscribe((listings) => {
        this.listings = listings;
      }),
    );
  }
}
